import { memo, Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { clsx } from 'clsx';
import { TextBlock } from '@library';
import HorizontalLayout from '../../../../core/components/layout/HorizontalLayout';
import type { FilterList } from '../../../queries/types';
import { ProductState } from '../../../queries/types';
import useClearAllFilters from '../../../hooks/useClearAllFilters';
import useSearchParamFilterHelper from '../../../hooks/useSearchParamFilterHelper';
import useSalePage from '../../../../core/queries/useSalePage';
import type { CategoryFilter, ProductVehicle } from '../../../types';
import useVehicle from '../../../hooks/useVehicle';
import useCurrentCategoryFilters from '../../../queries/useCurrentCategoryFilters';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';
import { useProductListOptionsContext } from '../context/ProductListOptionsContext';
import ProductAppliedFilterItem, {
    ProductAppliedFilterAvailabilityItem,
    ProductAppliedFilterItemPriceItem,
} from './ProductAppliedFilterItem';
import styles from './ProductAppliedFiltersList.module.css';

const sortOrder: string[] = [
    'PIM_ATT_TIRE_WIDTH_MOTO',
    'PIM_ATT_TIRE_HIGHT_MOTO',
    'PIM_ATT_DIAMETER',
    'PIM_ATT_MATCHCODE',
    'PIM_ATT_APPLICATION_RANGE',
    'PIM_ATT_LOAD_INDEX',
    'PIM_ATT_SPEEDINDEX',
    'q',
    'bestAvailability',
    'vehicleId',
    'bicycleId',
    'onSale',
    'states',
    'PIM_ATT_ERP_MANUFACTURER',
    'PIM_ATT_COLOUR_WEB',
    'PIM_ATT_SIZE',
    'PIM_ATT_SIZE_COMBINATION',
    'PIM_ATT_MODEL',
    'PIM_ATT_BIKES_FRAME_SIZES_WEB',
    'PIM_ATT_BIKES_MODEL_YEAR',
    'minPrice',
    'maxPrice',
];

const getCombinedFilterList = ({
    filters,
    bicycleId,
    vehicleInfo,
    search,
    onSale,
    sale,
    onStates,
    isNewLabel,
}: {
    filters: FilterList[];
    bicycleId?: string;
    vehicleInfo?: ProductVehicle;
    search?: string;
    onSale?: boolean;
    sale: string;
    onStates: ProductState[];
    isNewLabel: string;
}): FilterList[] => [
    ...filters,
    ...(bicycleId ? [{ name: 'bicycleId', values: [bicycleId] }] : []),
    ...(vehicleInfo
        ? [
              {
                  name: 'vehicleId',
                  values: [vehicleInfo.id],
                  label: formatVehicleName(vehicleInfo),
              },
          ]
        : []),
    ...(search ? [{ name: 'q', values: [search] }] : []),
    ...(onSale
        ? [{ name: 'onSale', values: [`${onSale}`], slug: `${sale}` }]
        : []),
    ...onStates.map(state => {
        return {
            name: 'states',
            values: [state],
            slug: state === ProductState.ON_SALE ? sale : isNewLabel,
        };
    }),
];

const formatVehicleName = (vehicle: ProductVehicle) => {
    return `${vehicle.manufacturerName} ${vehicle.model}`;
};

const getValueLabelForFilterValue = (
    name: string,
    value: string,
    filterData?: CategoryFilter
) => {
    if (!filterData || !filterData.values) {
        return value;
    }

    return filterData.valueLabels?.[filterData.values.indexOf(value)] ?? value;
};

const useFiltersData = (skip: boolean) => {
    const {
        category: { filters: categoryFilters },
    } = useCurrentCategoryFilters({
        includeValues: true,
        skip,
    });

    const intl = useIntl();
    const fixedFilters = [
        { field: 'q', name: intl.formatMessage({ id: 'search' }) },
        {
            field: 'PIM_ATT_ERP_MANUFACTURER',
            name: intl.formatMessage({ id: 'make' }),
        },
        { field: 'vehicleId', name: intl.formatMessage({ id: 'motorcycle' }) },
        { field: 'bicycleId', name: intl.formatMessage({ id: 'bicycle' }) },
        { field: 'onSale', name: intl.formatMessage({ id: 'onSale' }) },
        { field: 'states', name: '' },
    ];

    return {
        filtersData: [...categoryFilters, ...fixedFilters] as CategoryFilter[],
    };
};

const useCombinedFilterList = () => {
    const intl = useIntl();

    const {
        filters: { filters, bicycleId, search, states },
    } = useProductListOptionsContext();

    const { data } = useSalePage();
    const { vehicle } = useVehicle();

    return {
        filters: getCombinedFilterList({
            filters,
            bicycleId,
            vehicleInfo: vehicle,
            search,
            sale: data?.filter_headline ?? 'true',
            onStates: states,
            isNewLabel: intl.formatMessage({ id: 'is new' }),
        }),
    };
};

const ProductAppliedFiltersList = ({
    ignoreList = [],
    mobile = false,
    showAllFilters,
}: {
    mobile?: boolean;
    ignoreList?: string[];
    showAllFilters?: boolean;
}) => {
    const {
        filters: { minPrice, maxPrice, bestAvailability },
    } = useProductListOptionsContext();
    const searchParamHelper = useSearchParamFilterHelper();
    const clearFilters = useClearAllFilters(ignoreList);

    const { filters: allFilters } = useCombinedFilterList();

    const filters = allFilters.filter(
        filter => !ignoreList.includes(filter.name) || showAllFilters
    );

    const { filtersData } = useFiltersData(!filters.length);

    const cleanedList = filters
        .map(filter => {
            return {
                ...filter,
                filterData: filtersData.find(
                    item => item.field === filter.name
                ),
            };
        })
        .filter(filter => filter.filterData || showAllFilters)
        .sort((a, b) => sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name));

    const showFilters = cleanedList.length > 0;

    const showPriceFilter = minPrice || maxPrice;

    const showAnyFilters = showFilters || showPriceFilter || !!bestAvailability;

    if (!showAnyFilters) {
        return null;
    }

    const removeFilterItem = (name: string, value: string) => {
        const { updateCurrentValues } = searchParamHelper(name);
        updateCurrentValues(value, true);
    };

    return (
        <div className={styles.productAppliedFiltersList}>
            {showAnyFilters && (
                <div className={styles.productAppliedFiltersListItem}>
                    <div
                        role="button"
                        tabIndex={0}
                        className={clsx(
                            styles.filterItem,
                            styles.filterItemClearAll,
                            {
                                [styles.mobile]: mobile,
                            }
                        )}
                        onClick={clearFilters}
                        onKeyPress={e => {
                            if (['Enter', ' '].includes(e.key)) {
                                clearFilters();
                            }
                        }}
                    >
                        <TextBlock
                            tertiary
                            uppercase
                            lineHeightAsFontSize
                            data-test-class={'clearAll'}
                        >
                            <HorizontalLayout align="center">
                                <span>
                                    <FormattedMessage id="clear all" />
                                </span>
                            </HorizontalLayout>
                        </TextBlock>
                    </div>
                </div>
            )}
            {bestAvailability && (
                <ProductAppliedFilterAvailabilityItem
                    mobile={mobile}
                    bestAvailability={bestAvailability}
                />
            )}
            {cleanedList.map(({ name, values, slug, label, filterData }) => (
                <Fragment key={`${name}-filter-item`}>
                    {values.map(value => (
                        <ProductAppliedFilterItem
                            mobile={mobile}
                            key={`${name}-filter-item-value-${value}`}
                            name={filterData?.name ?? name}
                            value={getValueLabelForFilterValue(
                                name,
                                value,
                                filterData
                            )}
                            slug={slug}
                            label={label}
                            removeFilterItem={() =>
                                removeFilterItem(name, value)
                            }
                        />
                    ))}
                </Fragment>
            ))}
            {showPriceFilter && (
                <ProductAppliedFilterItemPriceItem
                    mobile={mobile}
                    minPrice={minPrice}
                    maxPrice={maxPrice}
                />
            )}
        </div>
    );
};

export default memo(withErrorBoundary(ProductAppliedFiltersList));
